import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import type { PostSummary } from "@/lib/site";
import { GiftCard } from "@/components/site/HomeBlocks";
import { SignupBox } from "@/components/site/SignupBox";

type Pick = PostSummary & { reason: string };
const QUESTIONS = [
  { key: "who", q: "Who is it for?", opts: [["her", "Her"], ["him", "Him"], ["teacher", "A teacher"], ["child", "A child"], ["kid-made", "My kids are making it"], ["friend", "A friend or neighbor"], ["host", "A party host"]] },
  { key: "budget", q: "What's your budget?", opts: [["under5", "Under $5"], ["under20", "Under $20"], ["any", "Doesn't matter"]] },
  { key: "time", q: "How much time do you have?", opts: [["quick", "Under an hour"], ["afternoon", "An afternoon"], ["weekend", "A weekend"]] },
] as const;

function Choice({ q, opts, value, onPick, id }: { q: string; opts: readonly (readonly [string, string])[]; value: string; onPick: (v: string) => void; id: string }) {
  return (
    <fieldset className="mt-8">
      <legend className="shop-h2 text-lg">{q}</legend>
      <div className="mt-3 flex flex-wrap gap-2">
        {opts.map(([v, label]) => (
          <button key={v} type="button" onClick={() => onPick(v)} data-testid={`finder-${id}-${v}`}
            className={`rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ${value === v ? "border-[#1C1917] bg-[#1C1917] text-white" : "border-[#E7E5E4] bg-white text-[#1C1917] hover:border-[#A8A29E]"}`}>{label}</button>
        ))}
      </div>
    </fieldset>
  );
}

export default function GiftFinderPage() {
  const [a, setA] = useState<Record<string, string>>({ who: "", budget: "", time: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [picks, setPicks] = useState<Pick[] | null>(null);
  const ready = a.who && a.budget && a.time;
  const find = async () => {
    setBusy(true); setErr("");
    try {
      setPicks((await api.post("/public/gift-finder", { ...a, note })).data.picks);
      setTimeout(() => document.querySelector("[data-testid='finder-results']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-7xl px-5 pt-12 sm:px-8 lg:pt-16" data-testid="gift-finder-page">
      <div className="max-w-2xl">
        <p className="shop-eyebrow">Gift Finder</p>
        <h1 className="shop-h1 mt-4 text-4xl sm:text-5xl">Find the right handmade gift in 3 taps.</h1>
        <p className="mt-4 text-base text-[#78716C] sm:text-lg">Tell us a little, and we'll pick four ideas from our step-by-step guides.</p>
        {QUESTIONS.map((x) => <Choice key={x.key} id={x.key} q={x.q} opts={x.opts} value={a[x.key]} onPick={(v) => setA({ ...a, [x.key]: v })} />)}
        <label className="mt-8 block">
          <span className="shop-h2 text-lg">Anything they love? <span className="text-sm font-medium text-[#78716C]">(optional)</span></span>
          <input className="field mt-3 w-full px-4 py-3" maxLength={120} placeholder="Candles, coffee, gardening..." value={note} onChange={(e) => setNote(e.target.value)} data-testid="finder-note" />
        </label>
        <button className="btn-solid mt-8" disabled={!ready || busy} onClick={find} data-testid="finder-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{busy ? "Finding ideas…" : "Show my gift ideas"}
        </button>
        {err && <p className="mt-4 text-sm font-semibold text-[#B3261E]" data-testid="finder-error">{err}</p>}
      </div>
      {picks && (
        <section className="mt-16" data-testid="finder-results">
          <h2 className="shop-h2 text-3xl">Your gift ideas</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {picks.map((p, i) => (
              <div key={p.slug} className="flex flex-col gap-3" data-testid={`finder-pick-${i}`}>
                <GiftCard post={p} index={i} />
                {p.reason && <p className="hand-note -rotate-1 px-1 text-xl leading-snug">{p.reason}</p>}
              </div>
            ))}
          </div>
          <div className="mt-20"><SignupBox source="gift-finder" /></div>
        </section>
      )}
    </div>
  );
}
